/**
 * Client do BFF para a verificação de contato do cadastro.
 *
 * O BFF (`workers/account-api`) é a fonte única dos canais exigidos e o dono
 * dos códigos: a tela só pergunta e reporta. Nenhuma função lança — falha de
 * rede, HTTP de erro e resposta ilegível viram `{ ok: false, error }`, que a
 * sub-etapa traduz para o usuário.
 */

export type VerificationChannel = 'email' | 'sms'

const BASE = (
  import.meta.env.VITE_ACCOUNT_API_BASE || 'https://alelo-sit-api.ysec-technology.workers.dev'
).replace(/\/+$/, '')

const NETWORK_ERROR = 'network_error'

type Failure = { ok: false; error: string }

export type ConfigResult = { ok: true; channels: VerificationChannel[] } | Failure

export type StartResult =
  | { ok: true; sessionId: string; verified: false }
  | { ok: true; sessionId: string; verified: true; token: string }
  | Failure

export type ConfirmResult =
  | { ok: true; verified: false }
  | { ok: true; verified: true; token: string }
  | Failure

export type StartInput = {
  cpf: string
  /** O passo que confirma o e-mail ainda não tem o telefone, e vice-versa: o
   *  BFF exige só o destinatário do canal pedido e acumula o resto na sessão. */
  email?: string
  phone?: string
  /** Ausente quando nenhum canal é exigido: o BFF já devolve o token. */
  channel?: VerificationChannel
  sessionId?: string
}

export type ConfirmInput = {
  sessionId: string
  channel: VerificationChannel
  code: string
}

type Envelope = Record<string, unknown> & { error?: string }

/**
 * `{ data }` em qualquer resposta lida, `{ error }` em falha de rede ou corpo
 * ilegível. Toda falha deixa rastro no console: o browser só registra a linha
 * genérica, e é aqui que dá para distinguir rede/CORS de uma resposta do BFF.
 */
async function request(
  path: string,
  init?: RequestInit,
): Promise<{ data: Envelope; status: number } | Failure> {
  const url = `${BASE}${path}`
  let response: Response
  try {
    response = await fetch(url, init)
  } catch (err) {
    console.error('[signup-verification] falha de rede em', url, err)
    return { ok: false, error: NETWORK_ERROR }
  }

  let data: Envelope
  try {
    data = (await response.json()) as Envelope
  } catch {
    data = {}
  }

  if (!response.ok) {
    console.error(
      '[signup-verification]',
      path,
      'respondeu',
      response.status,
      data.error ?? '(sem código)',
    )
    return { ok: false, error: data.error || `http_${response.status}` }
  }
  return { data, status: response.status }
}

function failed(
  result: { data: Envelope; status: number } | Failure,
): result is Failure {
  return 'ok' in result
}

/** `GET /api/signup/verification/config` — canais exigidos pela configuração vigente. */
export async function config(): Promise<ConfigResult> {
  const result = await request('/api/signup/verification/config')
  if (failed(result)) return result

  const channels = result.data.channels
  if (!Array.isArray(channels)) return { ok: false, error: 'invalid_response' }
  return {
    ok: true,
    channels: channels.filter(
      (channel): channel is VerificationChannel =>
        channel === 'email' || channel === 'sms',
    ),
  }
}

/** `POST /api/signup/verification/start` — envia o código do canal pedido. */
export async function start(input: StartInput): Promise<StartResult> {
  const result = await request('/api/signup/verification/start', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (failed(result)) return result

  const sessionId = String(result.data.sessionId || '')
  if (!sessionId) return { ok: false, error: 'invalid_response' }

  const token = result.data.token
  if (result.data.verified === true && typeof token === 'string') {
    return { ok: true, sessionId, verified: true, token }
  }
  return { ok: true, sessionId, verified: false }
}

/** `POST /api/signup/verification/confirm` — confere o código do canal. */
export async function confirm(input: ConfirmInput): Promise<ConfirmResult> {
  const result = await request('/api/signup/verification/confirm', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (failed(result)) return result

  const token = result.data.token
  if (result.data.verified === true && typeof token === 'string') {
    return { ok: true, verified: true, token }
  }
  return { ok: true, verified: false }
}

export type CpfStatus = 'exists' | 'available' | 'unknown'

/**
 * `POST /api/signup/cpf-status` — o CPF já tem cadastro? Nunca falha: rede,
 * throttle (429) ou resposta fora do contrato viram `unknown`, e o cadastro
 * segue — a Auth0 ainda recusa CPF existente no `signup()`.
 */
export async function checkCpfStatus(cpf: string): Promise<CpfStatus> {
  const result = await request('/api/signup/cpf-status', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ cpf: cpf.replace(/\D/g, '') }),
  })
  if (failed(result)) return 'unknown'

  const status = result.data.status
  return status === 'exists' || status === 'available' ? status : 'unknown'
}

export type ContactStatus = 'in_use' | 'available' | 'unknown'

/**
 * `POST /api/signup/contact-status` — o e-mail ou o celular já é de outra
 * conta? Nunca falha: rede, throttle (429) ou resposta fora do contrato viram
 * `unknown`, e o passo segue — a Auth0 ainda recusa no `signup()`.
 */
export async function checkContactStatus(
  contact: { email: string } | { phone: string },
): Promise<ContactStatus> {
  const result = await request('/api/signup/contact-status', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(contact),
  })
  if (failed(result)) return 'unknown'

  const status = result.data.status
  return status === 'in_use' || status === 'available' ? status : 'unknown'
}
